'use client';
import React, { useState } from 'react';
import DMSShell from '@/components/DMSShell';
import { TruckIcon, DocumentTextIcon, UsersIcon, CurrencyDollarIcon, ClockIcon, CheckCircleIcon, ArrowTrendingUpIcon,  } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '@/components/ui/AppIcon';


const stageData = [
  { stage: 'Intake', count: 3, color: '#887CFD' },
  { stage: 'Recon', count: 4, color: '#5347CE' },
  { stage: 'Listing', count: 3, color: '#4896FE' },
  { stage: 'Ready', count: 2, color: '#16A34A' },
  { stage: 'Sold', count: 5, color: '#D69E2E' },
];

const actionItems = [
  { id: 1, title: '2019 Honda Civic EX', sub: 'Recon stage — 12 days (amber alert)', area: 'INVENTORY', urgent: false, amber: true, value: 12, max: 14 },
  { id: 2, title: '2018 BMW 3 Series', sub: 'Recon stage — 18 days (overdue)', area: 'INVENTORY', urgent: true, amber: false, value: 18, max: 14 },
  { id: 3, title: 'Deal #D-003 — Maria Santos', sub: 'Financing stage — awaiting lender response', area: 'DEALS', urgent: false, amber: true, value: 3, max: 5 },
  { id: 4, title: 'Deal #D-001 — Sarah Mitchell', sub: 'New lead — no contact made yet', area: 'DEALS', urgent: false, amber: false, value: 1, max: 5 },
  { id: 5, title: '2021 Ford F-150 XLT', sub: 'Missing 3 checklist items before listing', area: 'INVENTORY', urgent: false, amber: false, value: 7, max: 10 },
  { id: 6, title: 'David Chen', sub: 'New lead — contacted today, follow up needed', area: 'CONTACTS', urgent: false, amber: false, value: 1, max: 3 },
];

const recentActivity = [
  { time: '2h ago', text: 'Deal #D-002 moved to Negotiating — James Kowalski', type: 'deal' },
  { time: '4h ago', text: '2023 Hyundai Tucson added to inventory (Listing stage)', type: 'inventory' },
  { time: 'Yesterday', text: 'Worksheet #WS-001 created for 2022 Toyota RAV4 LE', type: 'worksheet' },
  { time: 'Yesterday', text: 'Jennifer Lee added as new lead', type: 'contact' },
  { time: '2 days ago', text: 'Deal #D-005 closed — 2023 Hyundai Tucson sold', type: 'deal' },
];

const tabs = ['Action Stream', 'Overview', 'Turn Times', 'Daily Digest'];

function CircleGauge({ value, max, urgent, amber }: { value: number; max: number; urgent: boolean; amber: boolean }) {
  const pct = Math.min(value / max, 1);
  const r = 18;
  const circ = 2 * Math.PI * r;
  const dash = pct * circ;
  const color = urgent ? '#DC2626' : amber ? '#D69E2E' : '#5347CE';
  return (
    <svg width={44} height={44} viewBox="0 0 44 44">
      <circle cx={22} cy={22} r={r} fill="none" stroke="#E5E7EB" strokeWidth={4} />
      <circle
        cx={22} cy={22} r={r} fill="none"
        stroke={color} strokeWidth={4}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 22 22)"
      />
      <text x={22} y={27} textAnchor="middle" fontSize={12} fontWeight={700} fill={color}>{value}</text>
    </svg>
  );
}

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState('Action Stream');

  return (
    <DMSShell>
      {/* Page header */}
      <div className="page-header" style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 className="page-title">Dashboard</h1>
          <div style={{ fontSize: 12, color: 'var(--steel)' }}>Driveline Motors · {new Date().toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="tab-bar" style={{ marginTop: 8 }}>
        {tabs.map(t => (
          <button key={t} className={`tab-btn${activeTab === t ? ' active' : ''}`} onClick={() => setActiveTab(t)}>{t}</button>
        ))}
      </div>

      <div style={{ padding: '24px 28px' }}>
        {activeTab === 'Action Stream' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
            {/* Action items */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--steel)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {actionItems.length} items need attention
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {actionItems.map(item => (
                  <div
                    key={item.id}
                    className="card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '12px 16px',
                      borderLeft: `3px solid ${item.urgent ? 'var(--red)' : item.amber ? 'var(--warn)' : 'var(--steel-light)'}`,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                  >
                    <CircleGauge value={item.value} max={item.max} urgent={item.urgent} amber={item.amber} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-heading)', marginBottom: 2 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--steel)' }}>{item.sub}</div>
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--steel)', background: 'var(--cream)', padding: '3px 8px', borderRadius: 999 }}>
                      {item.area}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Total Inventory', value: '12', icon: TruckIcon, color: '#5347CE' },
                  { label: 'Active Deals', value: '5', icon: DocumentTextIcon, color: '#4896FE' },
                  { label: 'Contacts', value: '47', icon: UsersIcon, color: '#16A34A' },
                  { label: 'Revenue (MTD)', value: '$84.5K', icon: CurrencyDollarIcon, color: '#D69E2E' },
                ].map(s => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="stat-card">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon style={{ width: 15, height: 15, color: s.color }} />
                        </div>
                      </div>
                      <div className="stat-value" style={{ fontSize: 20 }}>{s.value}</div>
                      <div className="stat-label" style={{ marginTop: 2 }}>{s.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Stage chart */}
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-heading)', marginBottom: 12 }}>Inventory by Stage</div>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={stageData} barSize={28}>
                    <XAxis dataKey="stage" tick={{ fontSize: 11, fill: '#5C6670' }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                      cursor={{ fill: '#EEF0FF' }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {stageData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Recent activity */}
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-heading)', marginBottom: 12 }}>Recent Activity</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {recentActivity.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: a.type === 'deal' ? '#5347CE' : a.type === 'inventory' ? '#4896FE' : a.type === 'worksheet' ? '#D69E2E' : '#16A34A', marginTop: 5, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--ink)' }}>{a.text}</div>
                        <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2 }}>{a.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
              {[
                { label: 'Total Inventory', value: '12', sub: '3 need attention', color: '#5347CE' },
                { label: 'Active Deals', value: '5', sub: '2 in financing', color: '#4896FE' },
                { label: 'Total Contacts', value: '47', sub: '18 leads, 22 customers', color: '#16A34A' },
                { label: 'Revenue (MTD)', value: '$84,500', sub: '3 vehicles sold', color: '#D69E2E' },
              ].map(s => (
                <div key={s.label} className="stat-card">
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--steel)', marginTop: 4 }}>{s.sub}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div className="card" style={{ padding: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Stage Distribution</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stageData}>
                    <XAxis dataKey="stage" tick={{ fontSize: 11, fill: '#5C6670' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#5C6670' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} cursor={{ fill: '#EEF0FF' }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {stageData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card" style={{ padding: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Recent Activity</div>
                {recentActivity.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < recentActivity.length - 1 ? '1px solid var(--steel-light)' : 'none' }}>
                    <div style={{ fontSize: 12.5, color: 'var(--ink)', flex: 1 }}>{a.text}</div>
                    <div style={{ fontSize: 11, color: 'var(--steel)', whiteSpace: 'nowrap' }}>{a.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Turn Times' && (
          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Average Days Per Stage</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Avg Days</th>
                  <th>Amber Threshold</th>
                  <th>Red Threshold</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { stage: 'Intake', avg: 2.1, amber: 3, red: 7 },
                  { stage: 'Recon', avg: 9.4, amber: 7, red: 14 },
                  { stage: 'Listing', avg: 4.8, amber: 14, red: 30 },
                  { stage: 'Ready for Sale', avg: 3.2, amber: 5, red: 10 },
                  { stage: 'Sold', avg: 1.5, amber: 2, red: 5 },
                ].map(r => (
                  <tr key={r.stage}>
                    <td style={{ fontWeight: 600 }}>{r.stage}</td>
                    <td className="mono">{r.avg}d</td>
                    <td style={{ color: 'var(--warn)' }}>{r.amber}d</td>
                    <td style={{ color: 'var(--red)' }}>{r.red}d</td>
                    <td>
                      <span style={{
                        background: r.avg > r.red ? '#FEE2E2' : r.avg > r.amber ? '#FEF3C7' : '#D1FAE5',
                        color: r.avg > r.red ? '#991B1B' : r.avg > r.amber ? '#92400E' : '#065F46',
                        borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600
                      }}>
                        {r.avg > r.red ? 'Overdue' : r.avg > r.amber ? 'Amber' : 'On Track'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'Daily Digest' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { title: "Today's Appointments", count: 2, items: ['10:00am — Test drive with David Chen', '2:30pm — Delivery for James Kowalski'], icon: ClockIcon, color: '#4896FE' },
              { title: 'Tasks Due Today', count: 3, items: ['Complete recon on BMW 3 Series', 'Follow up with Sarah Mitchell', 'Upload photos for Hyundai Tucson'], icon: CheckCircleIcon, color: '#16A34A' },
              { title: 'Deals Closing Soon', count: 2, items: ['#D-003 Maria Santos — Financing pending', '#D-002 James Kowalski — Negotiating'], icon: ArrowTrendingUpIcon, color: '#5347CE' },
            ].map(card => {
              const Icon = card.icon;
              return (
                <div key={card.title} className="card" style={{ padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: card.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon style={{ width: 18, height: 18, color: card.color }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{card.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--steel)' }}>{card.count} items</div>
                    </div>
                  </div>
                  {card.items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, padding: '7px 0', borderTop: '1px solid var(--steel-light)', fontSize: 13, color: 'var(--ink)' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: card.color, marginTop: 5, flexShrink: 0 }} />
                      {item}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DMSShell>
  );
}
